import { tool } from 'ai'
import { BACKEND_URL } from '../config'
import { getAuthToken } from '../supabase'
import { fetchDataFromUrl, queryDatabase } from './common-tools'
import { getCsvAttachment, getCsvAttachmentCount } from './csv-store'
import { toolSchemas } from './tool-schemas'

/**
 * Tool definitions for Google Sheets operations using Vercel AI SDK 6
 * These tools enable the agent to read/write data, inspect sheet structure,
 * and manage formatting in the active Google Sheets spreadsheet.
 */

/**
 * Helper to execute a tool with consistent error handling and message emission
 */
async function executeTool<TResult>(
  appsScriptFunction: string,
  args: unknown[],
  transformResult: (result: TResult) => unknown,
  errorPrefix?: string
): Promise<unknown> {
  try {
    const result = await callAppsScript<TResult>(appsScriptFunction, ...args)
    return transformResult(result)
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err)
    const prefix =
      errorPrefix ?? `Failed to ${appsScriptFunction.toLowerCase()}`
    return { error: `${prefix}: ${errMsg}` }
  }
}

/**
 * Helper to call Google Apps Script functions
 * Since the React app runs in an iframe, we use postMessage to communicate
 * with the parent window which has access to google.script.run
 */
export function callAppsScript<T>(
  functionName: string,
  ...args: unknown[]
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    // Check if we're in an iframe (React app context)
    const isInIframe = window.self !== window.top

    if (isInIframe) {
      // Use postMessage to communicate with parent window
      const messageId = `apps-script-${Date.now()}-${Math.random().toString(36).substring(7)}`

      // Set up message listener for response
      const messageHandler = (event: MessageEvent) => {
        // Security: In production, verify event.origin
        // if (event.origin !== 'https://script.google.com') return;

        if (
          event.data &&
          event.data.type === 'APPS_SCRIPT_RESULT' &&
          event.data.id === messageId
        ) {
          window.removeEventListener('message', messageHandler)

          if (event.data.error) {
            console.error(
              `Apps Script function ${functionName} failed:`,
              event.data.error
            )
            reject(new Error(event.data.error))
          } else {
            const result = event.data.result as T
            if (result === null || result === undefined) {
              console.error(
                `Apps Script function ${functionName} returned null/undefined`,
                { functionName, args, eventData: event.data }
              )
              reject(
                new Error(
                  `Apps Script function ${functionName} returned null or undefined. This may indicate a communication issue or the function failed silently.`
                )
              )
            } else {
              console.log(`Apps Script function ${functionName} succeeded`, {
                resultType: typeof result,
                hasResult: true,
              })
              resolve(result)
            }
          }
        }
      }

      window.addEventListener('message', messageHandler)

      // Send message to parent window
      window.parent.postMessage(
        {
          type: 'APPS_SCRIPT_CALL',
          id: messageId,
          functionName,
          args,
        },
        '*' // In production, specify exact origin
      )

      // Timeout after 30 seconds
      setTimeout(() => {
        window.removeEventListener('message', messageHandler)
        reject(
          new Error(
            `Apps Script function ${functionName} timed out after 30 seconds`
          )
        )
      }, 30000)
    } else {
      // Direct call (fallback if not in iframe)
      if (
        typeof google === 'undefined' ||
        !google.script ||
        !google.script.run
      ) {
        reject(new Error('Google Apps Script runtime not available'))
        return
      }

      const runner = google.script.run
        .withSuccessHandler((result: T) => {
          resolve(result)
        })
        .withFailureHandler((error: Error) => {
          reject(error)
        })

      const func = (
        runner as unknown as Record<string, (...args: unknown[]) => void>
      )[functionName]
      if (typeof func !== 'function') {
        reject(new Error(`Function ${functionName} not found`))
        return
      }

      try {
        func.apply(runner, args)
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)))
      }
    }
  })
}

export const readCellRanges = tool({
  ...toolSchemas.readCellRanges,
  execute: async ({ addresses }) => {
    const results: Array<{
      address: string
      values: unknown[][]
      rowCount: number
      columnCount: number
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each address
    for (const address of addresses) {
      try {
        const result = await callAppsScript<{
          success: boolean
          values: unknown[][]
          rowCount: number
          columnCount: number
        }>('readRange', address)

        if (!result) {
          throw new Error(
            'Apps Script returned null or undefined. This may indicate a communication issue or the range address is invalid.'
          )
        }
        if (!result.values) {
          throw new Error(
            `Apps Script returned result without values property. Result structure: ${JSON.stringify(result)}`
          )
        }
        if (!Array.isArray(result.values)) {
          throw new Error(
            `Apps Script returned invalid values type. Expected array, got: ${typeof result.values}`
          )
        }

        results.push({
          address,
          values: result.values,
          rowCount: result.rowCount ?? result.values.length,
          columnCount: result.columnCount ?? result.values[0]?.length ?? 0,
        })
      } catch (err) {
        errors.push({
          address: address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const writeCellRanges = tool({
  ...toolSchemas.writeCellRanges,
  execute: async ({ items }) => {
    const results: Array<{
      address: string
      rowsWritten: number
      columnsWritten: number
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          rowsWritten: number
          columnsWritten: number
        }>('writeRange', item.address, item.values, item.autofit ?? true)

        results.push({
          address: result.address,
          rowsWritten: result.rowsWritten,
          columnsWritten: result.columnsWritten,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

/**
 * Paste a pre-parsed CSV attachment directly into the sheet.
 */
export const pasteAttachedCsvToSheet = tool({
  ...toolSchemas.pasteAttachedCsvToSheet,
  execute: async ({ fileIndex = 0, startCell = 'A1', autofit = true }) => {
    try {
      const count = getCsvAttachmentCount()
      if (count === 0) {
        return {
          error: 'No CSV attachments found. The user must attach a CSV file.',
        }
      }
      const csv = getCsvAttachment(fileIndex)
      if (!csv) {
        return {
          error: `CSV attachment at index ${fileIndex} not found. ${count} CSV file(s) available (0-indexed).`,
        }
      }

      const result = await callAppsScript<{
        success: boolean
        address: string
        rowsWritten: number
        columnsWritten: number
      }>('writeRange', startCell, csv.rows, autofit)

      return {
        success: true,
        filename: csv.filename,
        address: result.address,
        rowsWritten: result.rowsWritten,
        columnsWritten: result.columnsWritten,
      }
    } catch (err) {
      return {
        error: `Failed to paste CSV: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const setFormulaRanges = tool({
  ...toolSchemas.setFormulaRanges,
  execute: async ({ items }) => {
    const results: Array<{
      address: string
      formula: string
      rowCount: number
      columnCount: number
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        if (!item.formula.startsWith('=')) {
          errors.push({
            address: item.address,
            error: 'Formula must start with "="',
          })
          continue
        }

        const result = await callAppsScript<{
          success: boolean
          address: string
          formula: string
          rowCount: number
          columnCount: number
        }>('setFormulaRange', item.address, item.formula, item.autofit ?? true)

        results.push({
          address: result.address,
          formula: result.formula,
          rowCount: result.rowCount,
          columnCount: result.columnCount,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const readSheetContext = tool({
  ...toolSchemas.readSheetContext,
  execute: async ({ detailed }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      usedRange: string
      rowCount: number
      columnCount: number
      values?: unknown[][]
    }>('readSheetContext', [detailed ?? false], (result) => ({
      success: true,
      ...result,
    }))
  },
})

export const getCellFormat = tool({
  ...toolSchemas.getCellFormat,
  execute: async ({ addresses }) => {
    return executeTool<
      Array<{
        address: string
        format: {
          backgroundColor?: string | null
          fontBold?: boolean
          fontItalic?: boolean
          fontColor?: string | null
          fontSize?: number
          numberFormat?: string[][]
          horizontalAlignment?: string
          verticalAlignment?: string
          wrapText?: boolean
          rowHeight?: number
          columnWidth?: number
        }
      }>
    >('getCellFormat', [addresses], (results) => ({
      success: true,
      results,
    }))
  },
})

export const formatCells = tool({
  ...toolSchemas.formatCells,
  execute: async ({ items }) => {
    const results: Array<{ address: string; formattingApplied: string[] }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          formattingApplied: string[]
        }>('formatCells', item.address, item.formatting)

        results.push({
          address: result.address,
          formattingApplied: result.formattingApplied,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const getSheetDimensions = tool({
  ...toolSchemas.getSheetDimensions,
  execute: async ({ includeUsedRange }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      maxRows: number
      maxColumns: number
      usedRange?: string
      usedRows?: number
      usedColumns?: number
    }>('getSheetDimensions', [includeUsedRange ?? true], (result) => ({
      success: true,
      ...result,
    }))
  },
})

export const getSelectedRange = tool({
  ...toolSchemas.getSelectedRange,
  execute: async ({ includeValues }) => {
    return executeTool<{
      success: boolean
      address: string | null
      rowCount: number
      columnCount: number
      values?: unknown[][]
    }>('getSelectedRange', [includeValues ?? false], (result) => ({
      success: true,
      ...result,
    }))
  },
})

export const clearCellRange = tool({
  ...toolSchemas.clearCellRange,
  execute: async ({ items }) => {
    const results: Array<{ address: string; cleared: boolean }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          cleared: boolean
        }>('clearCellRange', item.address, item.clearFormats ?? false)

        results.push({
          address: result.address,
          cleared: result.cleared,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const insertRows = tool({
  ...toolSchemas.insertRows,
  execute: async ({ startRow, count, shiftDown }) => {
    return executeTool<{
      success: boolean
      rowsInserted: number
      startRow: number
    }>('insertRows', [startRow, count ?? 1, shiftDown ?? true], (result) => ({
      success: true,
      rowsInserted: result.rowsInserted,
      startRow: result.startRow,
    }))
  },
})

export const insertColumns = tool({
  ...toolSchemas.insertColumns,
  execute: async ({ startColumn, count, shiftRight }) => {
    return executeTool<{
      success: boolean
      columnsInserted: number
      startColumn: number
    }>(
      'insertColumns',
      [startColumn, count ?? 1, shiftRight ?? true],
      (result) => ({
        success: true,
        columnsInserted: result.columnsInserted,
        startColumn: result.startColumn,
      })
    )
  },
})

export const deleteRows = tool({
  ...toolSchemas.deleteRows,
  execute: async ({ startRow, count, shiftUp }) => {
    return executeTool<{
      success: boolean
      rowsDeleted: number
      startRow: number
    }>('deleteRows', [startRow, count ?? 1, shiftUp ?? true], (result) => ({
      success: true,
      rowsDeleted: result.rowsDeleted,
      startRow: result.startRow,
    }))
  },
})

export const deleteColumns = tool({
  ...toolSchemas.deleteColumns,
  execute: async ({ startColumn, count, shiftLeft }) => {
    return executeTool<{
      success: boolean
      columnsDeleted: number
      startColumn: number
    }>(
      'deleteColumns',
      [startColumn, count ?? 1, shiftLeft ?? true],
      (result) => ({
        success: true,
        columnsDeleted: result.columnsDeleted,
        startColumn: result.startColumn,
      })
    )
  },
})

export const createTable = tool({
  ...toolSchemas.createTable,
  execute: async ({ address, hasHeaders, tableName }) => {
    return executeTool<{
      success: boolean
      address: string
      tableName: string
    }>('createTable', [address, hasHeaders ?? true, tableName], (result) => ({
      success: true,
      address: result.address,
      tableName: result.tableName,
    }))
  },
})

export const sortRange = tool({
  ...toolSchemas.sortRange,
  execute: async ({ address, key, ascending, hasHeaders }) => {
    return executeTool<{
      success: boolean
      address: string
      sortedByColumn: number
      ascending: boolean
    }>(
      'sortRange',
      [address, key, ascending ?? true, hasHeaders ?? true],
      (result) => ({
        success: true,
        address: result.address,
        sortedByColumn: result.sortedByColumn,
        ascending: result.ascending,
      })
    )
  },
})

export const findAndReplace = tool({
  ...toolSchemas.findAndReplace,
  execute: async ({
    address,
    findText,
    replaceText,
    matchCase,
    matchEntireCell,
  }) => {
    return executeTool<{
      success: boolean
      findText: string
      replaceText: string
      replacements: number
    }>(
      'findAndReplace',
      [
        address || null,
        findText,
        replaceText,
        matchCase ?? false,
        matchEntireCell ?? false,
      ],
      (result) => ({
        success: true,
        findText: result.findText,
        replaceText: result.replaceText,
        replacements: result.replacements,
      })
    )
  },
})

export const mergeCells = tool({
  ...toolSchemas.mergeCells,
  execute: async ({ items }) => {
    const results: Array<{ address: string; merged: boolean }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          merged: boolean
        }>('mergeCells', item.address, item.across ?? false)

        results.push({
          address: result.address,
          merged: result.merged,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const unmergeCells = tool({
  ...toolSchemas.unmergeCells,
  execute: async ({ items }) => {
    const results: Array<{ address: string; unmerged: boolean }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          unmerged: boolean
        }>('unmergeCells', item.address)

        results.push({
          address: result.address,
          unmerged: result.unmerged,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const addHyperlink = tool({
  ...toolSchemas.addHyperlink,
  execute: async ({ items }) => {
    const results: Array<{
      address: string
      url: string
      displayText: string
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          url: string
          displayText: string
        }>('addHyperlink', item.address, item.url, item.displayText)

        results.push({
          address: result.address,
          url: result.url,
          displayText: result.displayText,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const listWorksheets = tool({
  ...toolSchemas.listWorksheets,
  execute: async () => {
    return executeTool<{
      success: boolean
      worksheets: Array<{ name: string; isActive: boolean }>
      count: number
    }>('listWorksheets', [], (result) => ({
      success: true,
      worksheets: result.worksheets,
      count: result.count,
    }))
  },
})

export const switchWorksheet = tool({
  ...toolSchemas.switchWorksheet,
  execute: async ({ sheetName }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      activated: boolean
    }>('switchWorksheet', [sheetName], (result) => ({
      success: true,
      sheetName: result.sheetName,
      activated: result.activated,
    }))
  },
})

export const createWorksheet = tool({
  ...toolSchemas.createWorksheet,
  execute: async ({ items }) => {
    const results: Array<{
      sheetName: string
      created: boolean
    }> = []
    const errors: Array<{ sheetName: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          sheetName: string
          created: boolean
        }>('createWorksheet', item.sheetName, item.position ?? undefined)

        results.push({
          sheetName: result.sheetName,
          created: result.created,
        })
      } catch (err) {
        errors.push({
          sheetName: item.sheetName,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const copyRange = tool({
  ...toolSchemas.copyRange,
  execute: async ({ sourceAddress, targetAddress, copyFormats }) => {
    return executeTool<{
      success: boolean
      sourceAddress: string
      targetAddress: string
      copied: boolean
    }>(
      'copyRange',
      [sourceAddress, targetAddress, copyFormats ?? true],
      (result) => ({
        success: true,
        sourceAddress: result.sourceAddress,
        targetAddress: result.targetAddress,
        copied: result.copied,
      })
    )
  },
})

export const createChart = tool({
  ...toolSchemas.createChart,
  execute: async ({
    dataRange,
    chartType,
    hasHeaders,
    title,
    position,
    width,
    height,
  }) => {
    return executeTool<{
      success: boolean
      chartType: string
      dataRange: string
      chartName: string
      title?: string
    }>(
      'createChart',
      [
        dataRange,
        chartType,
        hasHeaders ?? true,
        title,
        position,
        width,
        height,
      ],
      (result) => ({
        success: true,
        chartType: result.chartType,
        dataRange: result.dataRange,
        chartName: result.chartName,
        title: result.title,
      })
    )
  },
})

/**
 * Executes JavaScript code in the Apps Script context
 * SpreadsheetApp and other Google Apps Script APIs are available
 */
export const executeCode = tool({
  ...toolSchemas.executeCodeSheets,
  execute: async ({ code }) => {
    try {
      const result = await callAppsScript<{
        success: boolean
        result?: unknown
        error?: string
        stack?: string
      }>('executeCodeInAppsScript', code)

      if (result.success) {
        return {
          success: true,
          result: result.result,
        }
      } else {
        return {
          error: result.error || 'Code execution failed',
          stack: result.stack,
        }
      }
    } catch (err) {
      return {
        error: `Failed to execute code: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Creates a named range (Sheets only)
 */
export const createNamedRange = tool({
  ...toolSchemas.createNamedRange,
  execute: async ({ name, address }) => {
    return executeTool<{
      success: boolean
      name: string
      address: string
    }>('createNamedRange', [name, address], (result) => ({
      success: true,
      name: result.name,
      address: result.address,
    }))
  },
})

/**
 * Gets all named ranges (Sheets only)
 */
export const getNamedRanges = tool({
  ...toolSchemas.getNamedRanges,
  execute: async () => {
    return executeTool<{
      success: boolean
      namedRanges: Array<{ name: string; address: string }>
      count: number
    }>('getNamedRanges', [], (result) => ({
      success: true,
      namedRanges: result.namedRanges,
      count: result.count,
    }))
  },
})

/**
 * Sets a filter on a range (Sheets only)
 */
export const setFilter = tool({
  ...toolSchemas.setFilter,
  execute: async ({ address, columnIndex, criteria }) => {
    return executeTool<{
      success: boolean
      address: string
      filtered: boolean
    }>(
      'setFilter',
      [address, columnIndex ?? null, criteria ?? null],
      (result) => ({
        success: true,
        address: result.address,
        filtered: result.filtered,
      })
    )
  },
})

/**
 * Removes filter from a range (Sheets only)
 */
export const removeFilter = tool({
  ...toolSchemas.removeFilter,
  execute: async ({ address }) => {
    return executeTool<{
      success: boolean
      address: string
      removed: boolean
    }>('removeFilter', [address], (result) => ({
      success: true,
      address: result.address,
      removed: result.removed,
    }))
  },
})

/**
 * Adds a note to a cell (Sheets only)
 */
export const addNote = tool({
  ...toolSchemas.addNote,
  execute: async ({ items }) => {
    const results: Array<{
      address: string
      note: string
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    // Process each item
    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          note: string
        }>('addNote', item.address, item.note)

        results.push({
          address: result.address,
          note: result.note,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

/**
 * Gets notes from a range (Sheets only)
 */
export const getNotes = tool({
  ...toolSchemas.getNotes,
  execute: async ({ address }) => {
    return executeTool<{
      success: boolean
      notes: Array<{ address: string; note: string }>
      count: number
    }>('getNotes', [address ?? null], (result) => ({
      success: true,
      notes: result.notes,
      count: result.count,
    }))
  },
})

// Data Validation Tools
export const setDataValidation = tool({
  ...toolSchemas.setDataValidationSheets,
  execute: async ({ items }) => {
    const results: Array<{
      address: string
      validationType: string
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          validationType: string
        }>('setDataValidation', item.address, item.validation)

        results.push({
          address: result.address,
          validationType: result.validationType,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const clearDataValidation = tool({
  ...toolSchemas.clearDataValidationSheets,
  execute: async ({ items }) => {
    const results: Array<{ address: string; cleared: boolean }> = []
    const errors: Array<{ address: string; error: string }> = []

    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          cleared: boolean
        }>('clearDataValidation', item.address)

        results.push({
          address: result.address,
          cleared: result.cleared,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const getDataValidation = tool({
  ...toolSchemas.getDataValidationSheets,
  execute: async ({ address }) => {
    return executeTool<{
      success: boolean
      address: string
      hasValidation: boolean
      validation?: unknown
    }>('getDataValidation', [address], (result) => ({
      success: true,
      ...result,
    }))
  },
})

// Conditional Formatting Tools
export const addConditionalFormatRule = tool({
  ...toolSchemas.addConditionalFormatRuleSheets,
  execute: async ({ items }) => {
    const results: Array<{
      address: string
      ruleType: string
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          ruleType: string
        }>('addConditionalFormatRule', item.address, item.rule)

        results.push({
          address: result.address,
          ruleType: result.ruleType,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const clearConditionalFormatRules = tool({
  ...toolSchemas.clearConditionalFormatRulesSheets,
  execute: async ({ items }) => {
    const results: Array<{ address: string; cleared: boolean }> = []
    const errors: Array<{ address: string; error: string }> = []

    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          cleared: boolean
        }>('clearConditionalFormatRules', item.address)

        results.push({
          address: result.address,
          cleared: result.cleared,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const getConditionalFormatRules = tool({
  ...toolSchemas.getConditionalFormatRulesSheets,
  execute: async () => {
    return executeTool<{
      success: boolean
      rules: Array<unknown>
      count: number
    }>('getConditionalFormatRules', [], (result) => ({
      success: true,
      rules: result.rules,
      count: result.count,
    }))
  },
})

// Cell Borders Tools
export const setCellBorders = tool({
  ...toolSchemas.setCellBordersSheets,
  execute: async ({ items }) => {
    const results: Array<{
      address: string
      bordersApplied: string[]
    }> = []
    const errors: Array<{ address: string; error: string }> = []

    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          bordersApplied: string[]
        }>('setCellBorders', item.address, item.borders)

        results.push({
          address: result.address,
          bordersApplied: result.bordersApplied,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

export const clearCellBorders = tool({
  ...toolSchemas.clearCellBordersSheets,
  execute: async ({ items }) => {
    const results: Array<{ address: string; cleared: boolean }> = []
    const errors: Array<{ address: string; error: string }> = []

    for (const item of items) {
      try {
        const result = await callAppsScript<{
          success: boolean
          address: string
          cleared: boolean
        }>('clearCellBorders', item.address)

        results.push({
          address: result.address,
          cleared: result.cleared,
        })
      } catch (err) {
        errors.push({
          address: item.address,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return {
      success: true,
      results,
      ...(errors.length > 0 && { errors }),
    }
  },
})

// ============================================================================
// Ported from Excel: Worksheet, Range, and Formatting Operations
// ============================================================================

export const freezePanes = tool({
  ...toolSchemas.freezePanes,
  execute: async ({ address }) => {
    return executeTool<{
      success: boolean
      address: string
      frozen: boolean
    }>('freezePanes', [address], (result) => ({
      success: true,
      address: result.address,
      frozen: result.frozen,
    }))
  },
})

export const deleteWorksheet = tool({
  ...toolSchemas.deleteWorksheet,
  execute: async ({ sheetName }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      deleted: boolean
    }>('deleteWorksheet', [sheetName], (result) => ({
      success: true,
      sheetName: result.sheetName,
      deleted: result.deleted,
    }))
  },
})

export const renameWorksheet = tool({
  ...toolSchemas.renameWorksheet,
  execute: async ({ oldName, newName }) => {
    return executeTool<{
      success: boolean
      oldName: string
      newName: string
    }>('renameWorksheet', [oldName, newName], (result) => ({
      success: true,
      oldName: result.oldName,
      newName: result.newName,
    }))
  },
})

export const hideWorksheet = tool({
  ...toolSchemas.hideWorksheet,
  execute: async ({ sheetName }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      hidden: boolean
    }>('hideWorksheet', [sheetName], (result) => ({
      success: true,
      sheetName: result.sheetName,
      hidden: result.hidden,
    }))
  },
})

export const showWorksheet = tool({
  ...toolSchemas.showWorksheet,
  execute: async ({ sheetName }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      visible: boolean
    }>('showWorksheet', [sheetName], (result) => ({
      success: true,
      sheetName: result.sheetName,
      visible: result.visible,
    }))
  },
})

export const copyWorksheet = tool({
  ...toolSchemas.copyWorksheet,
  execute: async ({ sheetName, newSheetName, position }) => {
    return executeTool<{
      success: boolean
      sourceSheet: string
      newSheetName: string
    }>(
      'copyWorksheet',
      [sheetName, newSheetName ?? null, position ?? null],
      (result) => ({
        success: true,
        sourceSheet: result.sourceSheet,
        newSheetName: result.newSheetName,
      })
    )
  },
})

export const getUsedRange = tool({
  ...toolSchemas.getUsedRange,
  execute: async ({ address, valuesOnly }) => {
    return executeTool<{
      success: boolean
      address: string
      rowCount: number
      columnCount: number
    }>('getUsedRange', [address ?? null, valuesOnly ?? false], (result) => ({
      success: true,
      address: result.address,
      rowCount: result.rowCount,
      columnCount: result.columnCount,
    }))
  },
})

export const selectRange = tool({
  ...toolSchemas.selectRange,
  execute: async ({ address }) => {
    return executeTool<{
      success: boolean
      address: string
    }>('selectRange', [address], (result) => ({
      success: true,
      address: result.address,
    }))
  },
})

export const getActiveCell = tool({
  ...toolSchemas.getActiveCell,
  execute: async ({ includeValue }) => {
    return executeTool<{
      success: boolean
      address: string
      value?: unknown
    }>('getActiveCell', [includeValue ?? false], (result) => ({
      success: true,
      address: result.address,
      ...(result.value !== undefined && { value: result.value }),
    }))
  },
})

export const setColumnWidth = tool({
  ...toolSchemas.setColumnWidth,
  execute: async ({ address, width }) => {
    return executeTool<{
      success: boolean
      address: string
      width: number
    }>('setColumnWidth', [address, width], (result) => ({
      success: true,
      address: result.address,
      width: result.width,
    }))
  },
})

export const setRowHeight = tool({
  ...toolSchemas.setRowHeight,
  execute: async ({ address, height }) => {
    return executeTool<{
      success: boolean
      address: string
      height: number
    }>('setRowHeight', [address, height], (result) => ({
      success: true,
      address: result.address,
      height: result.height,
    }))
  },
})

export const autofitColumns = tool({
  ...toolSchemas.autofitColumns,
  execute: async ({ address }) => {
    return executeTool<{
      success: boolean
      address: string
    }>('autofitColumns', [address], (result) => ({
      success: true,
      address: result.address,
    }))
  },
})

export const autofitRows = tool({
  ...toolSchemas.autofitRows,
  execute: async ({ address }) => {
    return executeTool<{
      success: boolean
      address: string
    }>('autofitRows', [address], (result) => ({
      success: true,
      address: result.address,
    }))
  },
})

export const moveRange = tool({
  ...toolSchemas.moveRange,
  execute: async ({ sourceAddress, targetAddress }) => {
    return executeTool<{
      success: boolean
      sourceAddress: string
      targetAddress: string
    }>('moveRange', [sourceAddress, targetAddress], (result) => ({
      success: true,
      sourceAddress: result.sourceAddress,
      targetAddress: result.targetAddress,
    }))
  },
})

export const groupRange = tool({
  ...toolSchemas.groupRange,
  execute: async ({ address, groupBy }) => {
    return executeTool<{
      success: boolean
      address: string
      groupBy: string
    }>('groupRange', [address, groupBy], (result) => ({
      success: true,
      address: result.address,
      groupBy: result.groupBy,
    }))
  },
})

export const ungroupRange = tool({
  ...toolSchemas.ungroupRange,
  execute: async ({ address, groupBy }) => {
    return executeTool<{
      success: boolean
      address: string
      groupBy: string
    }>('ungroupRange', [address, groupBy], (result) => ({
      success: true,
      address: result.address,
      groupBy: result.groupBy,
    }))
  },
})

// ============================================================================
// Cross-platform tools: Sheet protection, Named range delete
// ============================================================================

export const protectSheet = tool({
  ...toolSchemas.protectSheet,
  execute: async ({ sheetName, description }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      protected: boolean
    }>('protectSheet', [sheetName ?? null, description ?? null], (result) => ({
      success: true,
      sheetName: result.sheetName,
      protected: result.protected,
    }))
  },
})

export const unprotectSheet = tool({
  ...toolSchemas.unprotectSheet,
  execute: async ({ sheetName }) => {
    return executeTool<{
      success: boolean
      sheetName: string
      unprotected: boolean
    }>('unprotectSheet', [sheetName ?? null], (result) => ({
      success: true,
      sheetName: result.sheetName,
      unprotected: result.unprotected,
    }))
  },
})

export const deleteNamedRange = tool({
  ...toolSchemas.deleteNamedRange,
  execute: async ({ name }) => {
    return executeTool<{
      success: boolean
      name: string
      deleted: boolean
    }>('deleteNamedRange', [name], (result) => ({
      success: true,
      name: result.name,
      deleted: result.deleted,
    }))
  },
})

/**
 * Query a database and write results directly to the spreadsheet.
 * More efficient than queryDatabase + writeCellRanges for large result sets.
 */
export const queryDatabaseToSheet = tool({
  ...toolSchemas.queryDatabaseToSheet,
  execute: async ({
    connectorId,
    query,
    targetAddress,
    includeHeaders,
    autofit,
  }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      // Query the database
      const response = await fetch(
        `${BACKEND_URL}/api/connectors/${connectorId}/execute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ action: 'query', params: { query } }),
        }
      )

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.error || `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      const rows = result.rows as Array<Record<string, unknown>>

      if (!rows || rows.length === 0) {
        return {
          success: true,
          message: 'Query returned no rows',
          rowCount: 0,
        }
      }

      // Get column names from the first row's keys (most reliable)
      const columnNames = Object.keys(rows[0])
      const values: Array<Array<string | number | null>> = []

      if (includeHeaders ?? true) {
        values.push(columnNames)
      }

      for (const row of rows) {
        const rowValues = columnNames.map((col) => {
          const val = row[col]
          if (val === null || val === undefined) return null
          if (typeof val === 'string' || typeof val === 'number') return val
          return String(val)
        })
        values.push(rowValues)
      }

      // Debug: log what we're about to write
      console.log('[queryDatabaseToSheet] values array:', {
        length: values.length,
        firstRow: values[0],
        firstRowLength: values[0]?.length,
      })

      // Write to Google Sheets via Apps Script
      const writeResult = await callAppsScript<{
        success: boolean
        address: string
        sheetName: string
        rowsWritten: number
        columnsWritten: number
      }>('writeRange', targetAddress, values, autofit ?? true)

      return {
        success: true,
        targetAddress: writeResult.address,
        sheetName: writeResult.sheetName,
        rowsWritten: writeResult.rowsWritten,
        columnsWritten: writeResult.columnsWritten,
        rowCount: rows.length,
        columnCount: columnNames.length,
        includesHeaders: includeHeaders ?? true,
        // Debug: include first row to verify data
        debugFirstRow: values[0],
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to query database to sheet: ${error}` }
    }
  },
})

/**
 * All available tools for the Google Sheets agent
 * Export as ToolSet object for Agent integration
 */
export const sheetsTools = {
  readCellRanges,
  writeCellRanges,
  setFormulaRanges,
  readSheetContext,
  getCellFormat,
  formatCells,
  getSheetDimensions,
  getSelectedRange,
  executeCode,
  clearCellRange,
  insertRows,
  insertColumns,
  deleteRows,
  deleteColumns,
  createTable,
  sortRange,
  findAndReplace,
  mergeCells,
  unmergeCells,
  addHyperlink,
  listWorksheets,
  switchWorksheet,
  createWorksheet,
  copyRange,
  createChart,
  fetchDataFromUrl,
  queryDatabase,
  queryDatabaseToSheet,
  pasteAttachedCsvToSheet,
  createNamedRange,
  getNamedRanges,
  setFilter,
  removeFilter,
  addNote,
  getNotes,
  setDataValidation,
  clearDataValidation,
  getDataValidation,
  addConditionalFormatRule,
  clearConditionalFormatRules,
  getConditionalFormatRules,
  setCellBorders,
  clearCellBorders,
  // Cross-platform
  protectSheet,
  unprotectSheet,
  deleteNamedRange,
  // Ported from Excel
  freezePanes,
  deleteWorksheet,
  renameWorksheet,
  hideWorksheet,
  showWorksheet,
  copyWorksheet,
  getUsedRange,
  selectRange,
  getActiveCell,
  setColumnWidth,
  setRowHeight,
  autofitColumns,
  autofitRows,
  moveRange,
  groupRange,
  ungroupRange,
}
